// frontend/src/hooks/useDynamicTranslation.js

import { useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from "../../contexts/AuthContext";

export const useDynamicTranslation = (originalText) => {
    const { i18n } = useTranslation();
    const { fetchWithAuth } = useContext(AuthContext);
    const [translatedText, setTranslatedText] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [, setTranslationError] = useState(null);
    const [isCheckingCache, setIsCheckingCache] = useState(false);

    useEffect(() => {
        let isMounted = true;
        
        // Reset state on text/language change
        setTranslatedText(null);
        setIsTranslating(false);
        setTranslationError(null);

        const checkCache = async () => {
            if (!originalText || i18n.language === 'en') {
                return;
            }
            setIsCheckingCache(true);
            try {
                const response = await fetchWithAuth('/api/translate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        text: originalText,
                        target_language: i18n.language,
                        cache_only: true,
                    }),
                });
                if (response.ok) {
                    const data = await response.json();
                    if (isMounted && data.translated_text) {
                        setTranslatedText(data.translated_text);
                    }
                }
            } catch (err) {
                console.error("Dynamic translation cache check failed:", err);
            } finally {
                if (isMounted) {
                    setIsCheckingCache(false);
                }
            }
        };

        checkCache();

        return () => { isMounted = false; };
    }, [originalText, i18n.language, fetchWithAuth]);

    const handleTranslate = useCallback(async () => {
        if (!originalText || i18n.language === 'en') return;
        setIsTranslating(true);
        setTranslationError(null);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: originalText,
                    target_language: i18n.language,
                }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Translation failed');
            setTranslatedText(data.translated_text);
        } catch (err) {
            setTranslationError(err.message);
        } finally {
            setIsTranslating(false);
        }
    }, [originalText, i18n.language, fetchWithAuth]);

    // --- FINALIZED RETURN LOGIC ---
    return {
        // The text to display in the main body
        textToShow: translatedText || originalText,
        // Is the user actively waiting for a manual translation?
        isTranslating,
        // Is the system checking the cache in the background?
        isCheckingCache,
        // The final translated text, or null if not available
        finalTranslation: translatedText,
        // The function to trigger a manual translation
        handleTranslate,
    };
};